import { useCallback, useEffect, useState } from 'react'
import type { MessageKey } from '@/i18n'
import { useSyncStatus, type TeamMember } from '@/offline'
import type { UnlockResult } from '@/auth/pin/phoneSession'
import {
  Avatar,
  Btn,
  Card,
  Cta,
  Footer,
  Grow,
  Icon,
  Keypad,
  SheetActions,
  V1Header,
  V1Screen,
  V1Sheet,
  WaitCta,
} from './kit'
import { C } from './style'
import { tr } from './text'

/**
 * V1 sign-in, as the map's PIN board draws it: who is working (only when the phone has more than
 * one person) → PIN डालें (four blind dots, keypad, PIN भूल गए, खोलें). The Welcome board is left
 * out on purpose. The PIN is checked on the phone (F4.1-t), as before.
 */
export function V1Unlock({
  team,
  village,
  pinLength,
  unlock,
  onDone,
}: {
  team: TeamMember[]
  village: string
  pinLength: number
  unlock: (userId: string, pin: string) => Promise<UnlockResult>
  onDone: () => void
}) {
  const [person, setPerson] = useState<TeamMember | null>(team.length === 1 ? team[0]! : null)
  if (!person) return <Who team={team} village={village} onPick={setPerson} />
  return (
    <Pin
      key={person.user_id}
      person={person}
      village={village}
      pinLength={pinLength}
      unlock={unlock}
      onDone={onDone}
      onBack={team.length > 1 ? () => setPerson(null) : undefined}
    />
  )
}

const roleLine = (m: TeamMember, village: string) => {
  const role = m.roles.find((r) => r === 'village_operator' || r === 'supervisor')
  return role ? `${village} · ${tr(`gate.v1.pin.role.${role}` as MessageKey)}` : village
}

// ---------------------------------------------------------------------------------------------

// ---------------------------------------------------------------------------------------------

function PersonCard({
  m,
  village,
  onClick,
}: {
  m: TeamMember
  village: string
  onClick?: () => void
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 10px',
        borderRadius: 12,
        background: C.surface,
        border: `1.5px solid ${C.rule}`,
        color: C.ink,
        boxSizing: 'border-box',
        width: '100%',
        textAlign: 'left',
        fontFamily: 'inherit',
        cursor: onClick ? 'pointer' : undefined,
      }}
    >
      <Avatar size={52} icon={44} />
      <span style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25 }}>{m.name}</span>
        <span style={{ fontSize: 15, color: C.muted, lineHeight: 1.3 }}>
          {roleLine(m, village)}
        </span>
      </span>
      {onClick && (
        <span style={{ color: C.acc, display: 'flex' }}>
          <Icon name="next" size={22} sw={2.5} />
        </span>
      )}
    </Tag>
  )
}

function Who({
  team,
  village,
  onPick,
}: {
  team: TeamMember[]
  village: string
  onPick: (m: TeamMember) => void
}) {
  const sync = useSyncStatus()
  return (
    <V1Screen
      header={<V1Header pending={sync.pending} icon="user" title={tr('gate.v1.pin.who')} />}
    >
      {team.map((m) => (
        <PersonCard key={m.user_id} m={m} village={village} onClick={() => onPick(m)} />
      ))}
    </V1Screen>
  )
}

function Pin({
  person,
  village,
  pinLength,
  unlock,
  onDone,
  onBack,
}: {
  person: TeamMember
  village: string
  pinLength: number
  unlock: (userId: string, pin: string) => Promise<UnlockResult>
  onDone: () => void
  onBack?: () => void
}) {
  const sync = useSyncStatus()
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [lockedUntil, setLockedUntil] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const [forgot, setForgot] = useState(false)
  const locked = lockedUntil > now

  useEffect(() => {
    if (!locked) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [locked])

  const onKey = useCallback(
    (k: string) => {
      if (busy || locked) return
      setMessage(null)
      setPin((p) => (k === '⌫' ? p.slice(0, -1) : p.length < pinLength ? p + k : p))
    },
    [busy, locked, pinLength],
  )

  const submit = async () => {
    if (pin.length < pinLength || busy || locked) return
    setBusy(true)
    const res = await unlock(person.user_id, pin)
    setBusy(false)
    if (res.ok) return onDone()
    setPin('')
    if (res.reason === 'wrong_pin') setMessage(tr('pin.wrong', { left: res.triesLeft }))
    else if (res.reason === 'locked_out') {
      setLockedUntil(Date.now() + res.retryInMs)
      setNow(Date.now())
    } else setMessage(tr('pin.noPinSet'))
  }

  const full = pin.length === pinLength
  return (
    <V1Screen
      header={
        <V1Header
          onBack={onBack}
          pending={sync.pending}
          icon="user"
          title={tr('gate.v1.pin.title')}
        />
      }
      main={{ gap: 8 }}
      footer={
        <Footer>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn icon="supervisor" onClick={() => setForgot(true)}>
              {tr('gate.v1.pin.forgot')}
            </Btn>
          </div>
          {full && !locked ? (
            <Cta
              label={tr('gate.v1.pin.open')}
              icon="ok"
              busy={busy}
              onClick={() => void submit()}
            />
          ) : (
            <WaitCta>{tr('gate.v1.pin.title')}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        forgot && (
          <V1Sheet
            title={tr('gate.v1.pin.forgot')}
            onClose={() => setForgot(false)}
            actions={
              <SheetActions>
                <Cta label={tr('gate.v1.help.gotIt')} icon="ok" onClick={() => setForgot(false)} />
              </SheetActions>
            }
          >
            <Card>
              <span style={{ fontSize: 17, lineHeight: 1.4 }}>{tr('gate.v1.pin.forgotText')}</span>
            </Card>
          </V1Sheet>
        )
      }
    >
      <PersonCard m={person} village={village} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          padding: '4px 0 0',
        }}
      >
        <span style={{ fontSize: 17, lineHeight: 1.3 }}>
          {tr('gate.v1.pin.press', { n: pinLength })}
        </span>
        <div
          role="img"
          aria-label={tr('gate.v1.pin.dots', { done: pin.length, n: pinLength })}
          style={{ display: 'flex', gap: 20 }}
        >
          {Array.from({ length: pinLength }, (_, i) => (
            <span
              key={i}
              style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                border: `2.5px solid ${C.ink}`,
                background: i < pin.length ? C.ink : 'transparent',
                boxSizing: 'border-box',
              }}
            />
          ))}
        </div>
      </div>
      {(message || locked) && (
        <div
          role="alert"
          style={{
            padding: '8px 12px',
            borderRadius: 12,
            background: C.badWash,
            color: C.bad,
            fontSize: 17,
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {locked
            ? tr('pin.lockedOut', { seconds: Math.ceil((lockedUntil - now) / 1000) })
            : message}
        </div>
      )}
      <Grow />
      <Keypad onKey={onKey} dot={false} />
    </V1Screen>
  )
}
